import React, { useState } from 'react';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  ButtonGroup,
  Toggle,
  IconButton,
  Tag
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import { Icon } from '@rsuite/icons';
import { MdPrint, MdQrCode2 } from 'react-icons/md';
import { mockSpaces } from '@/data/mock';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface DataTableProps {
  floorId: string;
}

const DataTable: React.FC<DataTableProps> = ({ floorId }) => {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedSpaces, setSelectedSpaces] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');

  // Get mock data for this floor
  const spaces = mockSpaces(floorId);

  // Filter spaces based on status and search
  const filteredSpaces = spaces.filter(space => {
    const matchesSearch = space.name.toLowerCase().includes(searchKeyword.toLowerCase()) ||
                         space.zone.toLowerCase().includes(searchKeyword.toLowerCase());
    
    if (filterStatus === 'all') return matchesSearch;
    if (filterStatus === 'available') return matchesSearch && space.availability;
    if (filterStatus === 'blocked') return matchesSearch && !space.availability;
    return matchesSearch;
  });

  const QRCodeCell = ({ rowData, ...props }) => (
    <Cell {...props} style={{ padding: '10px' }}>
      <Tag>
        <Icon as={MdQrCode2} style={{ fontSize: '24px' }} />
        <span style={{ marginLeft: '8px' }}>{rowData.qrCode}</span>
      </Tag>
    </Cell>
  );

  const ActionCell = ({ rowData, ...props }) => (
    <Cell {...props}>
      <IconButton
        icon={<Icon as={MdPrint} />}
        appearance="subtle"
        onClick={() => console.log('Print QR Code:', rowData.qrCode)}
      />
    </Cell>
  );

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t("Search")} value={searchKeyword} onChange={setSearchKeyword} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
        </Stack>

        <ButtonGroup>
          <Button 
            appearance={filterStatus === 'all' ? 'primary' : 'default'}
            onClick={() => setFilterStatus('all')}
          >
            All
          </Button>
          <Button 
            appearance={filterStatus === 'available' ? 'primary' : 'default'}
            onClick={() => setFilterStatus('available')}
          >
            Available
          </Button>
          <Button 
            appearance={filterStatus === 'blocked' ? 'primary' : 'default'}
            onClick={() => setFilterStatus('blocked')}
          >
            Blocked
          </Button>
        </ButtonGroup>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 200, 400)}
        data={filteredSpaces}
        id="table"
        rowHeight={80}
      >
        <Column width={50} align="center">
          <HeaderCell>
            <input 
              type="checkbox" 
              onChange={e => {
                setSelectedSpaces(e.target.checked ? spaces.map(s => s.id) : []);
              }}
            />
          </HeaderCell>
          <Cell>
            {rowData => (
              <input
                type="checkbox"
                checked={selectedSpaces.includes(rowData.id)}
                onChange={e => {
                  const newSelected = e.target.checked
                    ? [...selectedSpaces, rowData.id]
                    : selectedSpaces.filter(id => id !== rowData.id);
                  setSelectedSpaces(newSelected);
                }}
              />
            )}
          </Cell>
        </Column>

        <Column flexGrow={1}>
          <HeaderCell>NAME</HeaderCell>
          <Cell dataKey="name" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell>AVAILABILITY</HeaderCell>
          <Cell>
            {rowData => (
              <Toggle
                checked={rowData.availability}
                onChange={() => {
                  // Handle availability toggle
                }}
              />
            )}
          </Cell>
        </Column>

        <Column flexGrow={1}>
          <HeaderCell>ZONE</HeaderCell>
          <Cell dataKey="zone" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell>QR-CODE</HeaderCell>
          <QRCodeCell />
        </Column>

        <Column width={80}>
          <HeaderCell>ACTION</HeaderCell>
          <ActionCell />
        </Column>
      </Table>
    </>
  );
};

export default DataTable; 